/**
 * Where a processed document goes and what it is called. Pure functions, so
 * the naming rules are pinned by tests. See ARCHITECTURE.md, Drive layout.
 */
import { ACCOUNT_KINDS, type Extraction } from './extraction'

export const NEEDS_REVIEW = 'Needs Review'
export const REVIEW_THRESHOLD = 0.7

/** Drive allows almost anything; strip what breaks paths elsewhere when exported. */
export function cleanName(value: string): string {
  return value
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120)
}

/** Lower-case slug for ids. */
export function slug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

export function needsReview(x: Extraction): boolean {
  return x.confidence < REVIEW_THRESHOLD || !x.document_date || (x.doc_type !== 'other' && !x.institution)
}

/** Folder path under the Finance Buddy root, one segment per folder. */
export function folderPath(x: Extraction): string[] {
  if (needsReview(x)) return [NEEDS_REVIEW]
  const inst = cleanName(x.institution || 'Unknown')
  const year = (x.document_date ?? '').slice(0, 4)
  switch (x.doc_type) {
    case 'bank_statement':
      return ['Banking', inst, year]
    case 'credit_card_statement':
      return ['Credit Cards', inst, year]
    case 'brokerage_statement':
      return ['Investments', inst, year]
    case 'retirement_statement':
      return ['Retirement', inst, year]
    case 'loan_statement':
      return ['Loans', inst, year]
    case 'payslip':
      return ['Pay', inst, year]
    case 'tax_form':
      return ['Tax', year]
    case 'insurance':
      return ['Insurance', inst]
    case 'receipt':
      return ['Receipts', year]
    case 'other':
      return ['Other']
  }
}

function extension(originalName: string): string {
  const m = /\.[A-Za-z0-9]{1,5}$/.exec(originalName)
  return m ? m[0].toLowerCase() : ''
}

const STATEMENTS: Extraction['doc_type'][] = [
  'bank_statement',
  'credit_card_statement',
  'brokerage_statement',
  'retirement_statement',
  'loan_statement',
]

/** Drops leading words of `name` that already appear in `institution`. */
export function withoutInstitution(name: string, institution: string): string {
  const inst = new Set(slug(institution).split('-'))
  const words = name.split(/\s+/)
  while (words.length && inst.has(slug(words[0]))) words.shift()
  return words.join(' ')
}

/**
 * `<YYYY-MM-DD> <Institution> <title>.<ext>`. A single-account statement's
 * title comes from its account (`Rewards Visa Signature statement`), so every
 * statement for one account is named alike; combined statements and other
 * documents use Claude's short title. Documents headed for Needs Review keep
 * their original name until someone resolves them.
 */
export function fileName(x: Extraction, originalName: string): string {
  if (needsReview(x)) return originalName
  let title = x.short_title
  const only = x.accounts.length === 1 ? x.accounts[0] : undefined
  const product = only?.name ? withoutInstitution(only.name, x.institution) : ''
  if (STATEMENTS.includes(x.doc_type) && product) {
    title = /statement$/i.test(product) ? product : `${product} statement`
  }
  const parts = [x.document_date, x.institution, title].filter(Boolean).map((p) => cleanName(p!))
  return parts.join(' ') + extension(originalName)
}

type ExtractedAccount = Extraction['accounts'][number]

/** Stable across statements: `chase-credit-1234`, or the product name if no digits. */
export function accountId(institution: string, account: ExtractedAccount): string | undefined {
  if (!institution) return undefined
  const tail = account.last4 || account.name || 'unknown'
  return [slug(institution), account.kind, slug(tail)].join('-')
}

/** An Accounts row as read from the Sheet. */
export interface KnownAccount {
  account_id: string
  institution: string
  kind: string
  name: string
  last4: string
}

/**
 * Claude names the same institution differently from one document to the
 * next ("Fidelity (Elan Financial Services)", "Fidelity / Elan Financial
 * Services"). The Accounts tab is the authority: an account that matches a
 * known one by kind + last 4 (or by id) takes that row's id and product name,
 * and the document takes that row's institution, so it files alongside its
 * siblings — and an institution renamed by hand in the Sheet is what later
 * files follow. `accountIds` lines up with `extraction.accounts`.
 */
export function canonicalize(x: Extraction, known: KnownAccount[]): { extraction: Extraction; accountIds: (string | undefined)[] } {
  // Claude can call one account "brokerage" in one statement and "checking"
  // in the next, so after kind + last 4, the same bank + last 4 wins. Every
  // rule stays within the bank: a Citi card and a Chase card can share a last 4.
  const sameBank = (k: KnownAccount) => brand(k.institution) === brand(x.institution)
  const matches = x.accounts.map(
    (a) =>
      known.find((k) => a.last4 && k.kind === a.kind && k.last4 === a.last4 && sameBank(k)) ??
      known.find((k) => a.last4 && k.last4 === a.last4 && sameBank(k)) ??
      known.find((k) => k.account_id === accountId(x.institution, a)) ??
      // A card that prints no account number ("Apple Card" from "Apple" or
      // "Apple Card") is matched by kind and product name within the brand.
      known.find((k) => !a.last4 && !k.last4 && a.name && k.kind === a.kind && slug(k.name) === slug(a.name) && sameBank(k)),
  )
  const institution =
    matches.find((m) => m?.institution)?.institution ??
    known.find((k) => x.institution && slug(k.institution) === slug(x.institution))?.institution ??
    x.institution
  const accounts = x.accounts.map((a, i) => ({ ...a, name: matches[i]?.name || a.name }))
  const accountIds = accounts.map((a, i) => matches[i]?.account_id ?? accountId(institution, a))
  return { extraction: { ...x, institution, accounts }, accountIds }
}

/** Words that say nothing about which bank it is. */
const GENERIC = new Set(['bank', 'the', 'of', 'first', 'national', 'na'])

/**
 * First distinctive word of an institution: "Ally Bank" and "Ally" are one
 * bank, while "Bank of America" (america) and "Bank of the West" (west) are not.
 */
export function brand(institution: string): string {
  const words = slug(institution).split('-').filter(Boolean)
  return words.find((w) => !GENERIC.has(w)) ?? words[0] ?? ''
}

const KIND_SUFFIX = new RegExp(`-(${ACCOUNT_KINDS.join('|')})$`)

/**
 * One account however its id was spelled: bank brand plus last four digits.
 * The same Fidelity account has been filed as both fidelity-brokerage-2898
 * and fidelity-checking-2898. Ids with no trailing four digits (a 401(k)
 * plan, an Apple Card) are kept whole.
 */
export function accountKey(id: string): string {
  const m = /^(.+)-(\d{4})$/.exec(id.trim().toLowerCase())
  return m ? `${brand(m[1].replace(KIND_SUFFIX, ''))}-${m[2]}` : id.trim().toLowerCase()
}

/** The account a balance, transaction or holding belongs to, by its last4 tag. */
export function accountFor(x: Extraction, ids: (string | undefined)[], last4: string): string | undefined {
  const i = x.accounts.findIndex((a) => a.last4 && a.last4 === last4)
  return i >= 0 ? ids[i] : ids[0]
}
